import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService, DataService } from '../services/index'
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { LocationStrategy } from '@angular/common';
import { User,Error } from '../models/index'
import { UserService } from '../services/user.service'
import { Base64 } from 'js-base64';
import {
  EnabledPaymentOption,
  PaymentOptionsService
} from '../core/config/payment-options.service';
import { WhitelabelConfigStore } from '../core/config/whitelabel-config.store';
import { COUNTRY_OPTIONS } from './country-options';
import {
  PassengerDocumentTypeConfig,
  PassengerPhoneCountryConfig
} from '../core/config/whitelabel-config.model';
import {
  LEGACY_PAYMENT_FORM_STORAGE_KEY,
  PAYMENT_FLOW_STEP_STORAGE_KEY,
  PAYMENT_FLOW_STORAGE_KEY,
  StoredPaymentFlow
} from './payment-flow.storage';
import {
  SEAT_CONFLICT_RECOVERY_STORAGE_KEY,
  SeatConflictRecovery
} from '../core/booking/seat-conflict-recovery';

interface BookingSummaryPassenger {
  fullName: string;
  phoneNumber: string;
  seatNumber: string;
  amount: number;
}

interface JourneyFareSummary {
  label: string;
  details: any;
  from: string;
  to: string;
  date: string;
  seats: any[];
  fareGroups: Array<{ label: string; count: number; total: number }>;
  bookingFee: number;
  total: number;
}

@Component({
    selector: 'app-payments',
    templateUrl: './payments.component.html',
    styleUrl: './payments-redesign.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PaymentsComponent implements OnInit, OnDestroy {
  private sub: any;
  bookingdetails:any;
  returndetails:any;
  bookingdata:any={};
  return_trip:any;
  passengers=[];
  fsname:any;
  lsname:any;
  isdno:any;
  residence:any;
  phoneno:any;
  country:any;
  returnpassengers=[];
  bookingdataReturn:any={};
  formValidation=[];
  isError:boolean=false;
  errorMessage:any;
  res:any;
  is_return:false;
  loading=false;
  paymentForm: UntypedFormGroup;
  submitted = false;
  formDataObj={}
  copyMessage='Copy traveller 1 details';

  isLoggedIn:any;
  user:User = new User();
  api_error:Error;
  loadingStart:boolean = false;
  resMsg:any
  accepted_terms=false;
  selectedCountryCode: string = '254';  // Default to Kenya's country code


  selectedPaymentMethod = '';
  selectedPaymentCurrency: 'KSH' | 'UGX' = 'KSH';
  availablePaymentCurrencies: Array<'KSH' | 'UGX'> = [];
  enabledPaymentOptions: EnabledPaymentOption[] = [];
  paymentOptionsLoading = false;
  paymentOptionsError = '';
  currentStep: 'passenger' | 'payment' = 'passenger';
  expandedPassenger = 0;
  editingBoarding = false;
  draftPickupLocation = '';
  draftDropoffLocation = '';
  seatConflictOpen = false;
  seatConflictMessage = '';
  readonly countries = COUNTRY_OPTIONS;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private service:PaymentService,
    private dataService:DataService,
    private locationStrategy: LocationStrategy,
    private _fb: UntypedFormBuilder,
    private users:UserService,
    private paymentOptions: PaymentOptionsService,
    public readonly configStore: WhitelabelConfigStore
  ) { }

  get config() {
    return this.configStore.snapshot.config;
  }

  get documentTypes(): PassengerDocumentTypeConfig[] {
    return this.config?.passengerValidation?.documentTypes || [
      { value: 'national_id', label: 'National ID' },
      { value: 'passport', label: 'Passport' }
    ];
  }

  get passengerPhoneCountries(): PassengerPhoneCountryConfig[] {
    return this.config?.passengerValidation?.phoneCountries || [
      { name: 'Kenya', dialCode: '254', placeholder: '712 345 678' },
      { name: 'Uganda', dialCode: '256', placeholder: '772 123 456' }
    ];
  }

  passengerPhonePlaceholder(seat: any): string {
    const code = String(this.paymentForm?.get('phonecode' + seat.id)?.value || '');
    return this.passengerPhoneCountries.find(country => country.dialCode === code)?.placeholder ||
      'Phone number';
  }

  normalizePassengerPhone(value: unknown, dialCode: unknown): string {
    const raw = String(value || '').trim();
    const digits = raw.replace(/\D/g, '');
    const selectedCode = String(dialCode || '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    if (raw.startsWith('+')) {
      return digits;
    }
    if (selectedCode && digits.startsWith(selectedCode)) {
      return digits;
    }
    return selectedCode + digits.replace(/^0+/, '');
  }

  private phoneCountryCodeFor(value: unknown): string {
    const digits = String(value || '').replace(/\D/g, '');
    const matchingCountry = this.passengerPhoneCountries.find(country =>
      digits.startsWith(country.dialCode)
    );
    return matchingCountry?.dialCode ||
      this.config?.passengerValidation?.defaultPhoneCountryCode ||
      '254';
  }

  private passengerNameValidators(): ValidatorFn[] {
    const minLength = this.config?.passengerValidation?.nameMinLength || 2;
    return [
      Validators.required,
      Validators.minLength(minLength),
      Validators.pattern(/^[\p{L}\p{M}]+(?:[ '’\-][\p{L}\p{M}]+)*$/u)
    ];
  }

  private documentNumberValidators(): ValidatorFn[] {
    const rules = this.config?.passengerValidation;
    return [
      Validators.required,
      Validators.minLength(rules?.documentNumberMinLength || 4),
      Validators.maxLength(rules?.documentNumberMaxLength || 30),
      Validators.pattern(/^[\p{L}\p{N}][\p{L}\p{N} ./'\-]*$/u)
    ];
  }

  private internationalPhoneValidator(): ValidatorFn {
    const minDigits = this.config?.passengerValidation?.phoneMinDigits || 7;
    const maxDigits = this.config?.passengerValidation?.phoneMaxDigits || 15;
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control.value || '').trim();
      if (!raw) {
        return null;
      }
      if (!/^\+?[0-9 ()-]+$/.test(raw)) {
        return { internationalPhone: true };
      }
      const digitCount = raw.replace(/\D/g, '').length;
      return digitCount >= minDigits && digitCount <= maxDigits
        ? null
        : { internationalPhone: true };
    };
  }

  ngOnInit() {
    this.initForm();
    this.preventBackButton();
    this.isLoggedIn = sessionStorage.getItem('isLoggedInEasy');
    if(this.isLoggedIn ){
      this.user=new User().deserialize(JSON.parse(sessionStorage.getItem('userEasy')))
      this.getUserProfile(this.user);
    }

    this.sub = this.dataService.currentMessage.subscribe(message =>{

      if(!message){
        message = localStorage.getItem('payments-mashpoa')
        if(!message) {
         this.router.navigate(['/'])
       }
      }else{
        localStorage.setItem('payments-mashpoa', message)
      }

      let encoded =JSON.parse(Base64.decode(message))
      this.bookingdetails = JSON.parse(Base64.decode(encoded.travel));
      if(encoded.returning){
        this.returndetails = JSON.parse(Base64.decode(encoded.return));
      }


      this.is_return=encoded.returning;
      this.configurePaymentCurrencies();
      this.loadPaymentOptions();
      this.passengerSeats.forEach((seat) => {
        let fsname='fname'+seat.id;
        let lsname='lname'+seat.id;
        let isdno='idno'+seat.id;
        let doctype='doctype'+seat.id;
        let phoneno='phoneno'+seat.id;
        let phonecode='phonecode'+seat.id;
        let residence='residence'+seat.id;
        let assistance='assistance'+seat.id;
        this.formDataObj[fsname]=[this.user.first_name, this.passengerNameValidators()];
        this.formDataObj[lsname]=[this.user.last_name, this.passengerNameValidators()];
        this.formDataObj[doctype]=[
          this.config?.passengerValidation?.defaultDocumentType || 'national_id',
          Validators.required
        ];
        this.formDataObj[phoneno]=[this.user.phone_number, [Validators.required, this.internationalPhoneValidator()]];
        this.formDataObj[phonecode]=[
          this.phoneCountryCodeFor(this.user.phone_number),
          Validators.required
        ];
        this.formDataObj[residence]=[
          '',
          Validators.required
        ];
        // National ID/passport is deliberately never restored from browser storage.
        this.formDataObj[isdno]=['', this.documentNumberValidators()];
        this.formDataObj[assistance]=[''];
      });
    });
    this.formDataObj['email']=[this.user.email, [Validators.required, Validators.email]];
    this.formDataObj['mobileNumber']=[this.user.phone_number, [Validators.required, Validators.minLength(9),Validators.pattern('^(?:254|\\+254|0)?(7|1)[0-9]{8}$')]];
    this.formDataObj['countryCode']=['254', [Validators.required]];
    this.formDataObj['paymentMethod'] = [this.selectedPaymentMethod, Validators.required],

    // Disable inputs for MTN and Airtel initially
    this.paymentForm =this._fb.group(this.formDataObj);
    const transientFormState = this.dataService.consumePaymentFormState();
    if (transientFormState) {
      this.paymentForm.patchValue(transientFormState);
      this.restoreSeatConflictPassengers(transientFormState);
    }
    this.restorePaymentFlow();
    this.savePaymentFlow();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.clearPaymentFlowStorage();
  }


  initForm() {
    this.paymentForm = this._fb.group({
      mobileNumber: ['', [Validators.required, Validators.minLength(9),Validators.pattern('^(?:254|\\+254|0)?(7|1)[0-9]{8}$')]],
      paymentMethod: [this.selectedPaymentMethod, Validators.required],
    });

    this.disableInputsExcept('mobileNumber');
  }



  onPaymentMethodChange(paymentMethod: string) {
    this.selectedPaymentMethod = paymentMethod;
    this.disableInputsExcept(paymentMethod + 'Number');

    // Dial code and currency are properties of the selected payment option
    // (e.g. Airtel Money Kenya vs Airtel Money Uganda), never guessed from
    // the provider name - see PaymentOptionsService.
    this.selectedCountryCode = this.paymentOptionFor(paymentMethod)?.dialCode || '254';
    this.paymentForm.get('countryCode').setValue(this.selectedCountryCode);
  }

  private paymentOptionFor(paymentMethod: string): EnabledPaymentOption | undefined {
    return this.enabledPaymentOptions.find(option => option.formValue === paymentMethod);
  }

  onPaymentCurrencyChange(currency: 'KSH' | 'UGX'): void {
    if (currency === this.selectedPaymentCurrency) {
      return;
    }
    this.selectedPaymentCurrency = currency;
    this.loadPaymentOptions();
  }

  private configurePaymentCurrencies(): void {
    this.availablePaymentCurrencies = [
      ...(this.baseAmountForCurrency('KSH') > 0 ? ['KSH' as const] : []),
      ...(this.baseAmountForCurrency('UGX') > 0 ? ['UGX' as const] : [])
    ];
    this.selectedPaymentCurrency = this.availablePaymentCurrencies[0] || 'KSH';
  }

  private loadPaymentOptions(): void {
    const result = this.bookingdetails?.result;
    const operator = String(result?.operator?.alias || result?.operator_alias || '');
    if (!result || !operator || !this.availablePaymentCurrencies.includes(this.selectedPaymentCurrency)) {
      this.enabledPaymentOptions = [];
      this.selectedPaymentMethod = '';
      return;
    }

    this.paymentOptionsLoading = true;
    this.paymentOptionsError = '';
    this.enabledPaymentOptions = [];
    this.selectedPaymentMethod = '';
    this.paymentForm?.get('paymentMethod')?.setValue('');
    const requestedCurrency = this.selectedPaymentCurrency;

    this.service.getPaymentOptions({
      amount: this.baseAmountForCurrency(requestedCurrency),
      operator,
      leavingFrom: this.routeFrom,
      goingTo: this.routeTo,
      currency: requestedCurrency
    }).subscribe({
      next: response => {
        if (requestedCurrency !== this.selectedPaymentCurrency) {
          return;
        }
        this.enabledPaymentOptions = this.paymentOptions.fromCurrencyAwareResponse(
          response?.data?.payment_options || [],
          requestedCurrency
        );
        const first = this.enabledPaymentOptions[0];
        this.selectedPaymentMethod = first?.formValue || '';
        this.selectedCountryCode = first?.dialCode ||
          (this.selectedPaymentCurrency === 'UGX' ? '256' : '254');
        this.paymentForm?.patchValue({
          paymentMethod: this.selectedPaymentMethod,
          countryCode: this.selectedCountryCode
        });
        this.paymentOptionsLoading = false;
      },
      error: () => {
        if (requestedCurrency !== this.selectedPaymentCurrency) {
          return;
        }
        this.paymentOptionsLoading = false;
        this.paymentOptionsError = 'We could not load payment methods. Please try again.';
      }
    });
  }

  /** True when the selected payment option settles in UGX (e.g. Airtel Uganda, MTN Uganda). */
  private isUgandaPayment(paymentMethod: string): boolean {
    return this.paymentOptionFor(paymentMethod)?.currency === 'UGX';
  }

  get payableCurrency(): 'KSH' | 'UGX' {
    return this.paymentOptionFor(this.selectedPaymentMethod)?.currency || this.selectedPaymentCurrency;
  }

  get payableCurrencyLabel(): string {
    return this.payableCurrency === 'UGX' ? 'UGX' : 'Ksh';
  }

  get payableAmount(): number {
    return this.baseAmountForCurrency(this.payableCurrency) + this.paymentExtraCharge;
  }

  get paymentExtraCharge(): number {
    return this.paymentOptionFor(this.selectedPaymentMethod)?.extraCharge || 0;
  }

  /** The fare portion of payableAmount, before any provider surcharge is added. */
  get payableFareAmount(): number {
    return this.baseAmountForCurrency(this.payableCurrency);
  }

  /** The currency to offer switching to when the selected one has no method, or as a shortcut in the currency tabs. */
  get otherPaymentCurrency(): 'KSH' | 'UGX' | null {
    return this.availablePaymentCurrencies.find(currency => currency !== this.selectedPaymentCurrency) || null;
  }

  /** First word of the selected provider's name, e.g. "Airtel Money (Kenya)" → "Airtel". */
  get paymentMethodShortName(): string {
    return (this.paymentOptionFor(this.selectedPaymentMethod)?.name || 'mobile money').split(' ')[0];
  }

  get paymentMethodCountLabel(): string {
    if (this.paymentOptionsLoading) {
      return 'Loading…';
    }
    const count = this.enabledPaymentOptions.length;
    return `${count} ${count === 1 ? 'method accepts' : 'methods accept'} ${this.currencyCountryCode(this.selectedPaymentCurrency)}`;
  }

  currencyDisplayName(currency: 'KSH' | 'UGX'): string {
    return currency === 'UGX' ? 'Ugandan shillings' : 'Kenyan shillings';
  }

  /** Country code shown on the currency picker, e.g. KE instead of KSH. */
  currencyCountryCode(currency: 'KSH' | 'UGX'): string {
    return currency === 'UGX' ? 'UG' : 'KE';
  }

  /** Provider logos to preview beside a currency's country code (Mpesa+Airtel for KE, MTN+Airtel for UG). */
  currencyLogos(currency: 'KSH' | 'UGX'): string[] {
    return this.paymentOptions.logosForCurrency(currency);
  }

  methodSubLabel(option: EnabledPaymentOption): string {
    return `STK push to your ${option.name.split(' ')[0]} number`;
  }

  methodFeeLabel(option: EnabledPaymentOption): string {
    return option.extraCharge > 0
      ? `+ ${option.currency === 'UGX' ? 'UGX' : 'Ksh'} ${option.extraCharge.toLocaleString('en-KE')}`
      : 'No extra charge';
  }

  private baseAmountForCurrency(currency: 'KSH' | 'UGX'): number {
    return currency === 'UGX'
      ? Number(this.bookingdetails?.totalfareUGX || 0) + Number(this.returndetails?.totalfareUGX || 0)
      : this.totalPayableKSH;
  }

  disableInputsExcept(activeControlName: string) {
    // this.paymentForm.get('mobileNumber')?.disable();
    // this.paymentForm.get('mtnNumber')?.disable();
    // this.paymentForm.get('airtelNumber')?.disable();

    // Enable only the selected control
    this.paymentForm.get(activeControlName)?.enable();
  }

  getUserProfile(user:any){
    this.users.getProfile(user).subscribe(
      response => {
          this.loadingStart=false;
          this.resMsg=response;
          const profile = {
            ...user,
            ...(this.resMsg.data || {}),
            token: this.resMsg.data?.token || user.token
          };
          localStorage.setItem('userEasy', JSON.stringify(profile));
          sessionStorage.setItem('userEasy', JSON.stringify(profile));
      },
      error => {
        localStorage.removeItem("userEasy");
        // this.isLoggedIn=false;
      }
    );
  }

  preventBackButton() {
    history.pushState(null, null, location.href);
    this.locationStrategy.onPopState(() => {
      history.pushState(null, null, location.href);
    })
  }

  get f() { return this.paymentForm.controls; }

  get routeFrom(): string {
    return this.bookingdetails?.search_from || this.bookingdetails?.result?.from || '';
  }

  get routeTo(): string {
    return this.bookingdetails?.search_to || this.bookingdetails?.result?.to || '';
  }

  get journeyDateLabel(): string {
    return this.journeyDate(this.bookingdetails);
  }

  journeyDate(details: any): string {
    const raw = String(details?.result?.departure_date || details?.search_details?.traveldate || '');
    let date: Date | null = null;
    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const localMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (isoMatch) {
      date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    } else if (localMatch) {
      date = new Date(Number(localMatch[3]), Number(localMatch[2]) - 1, Number(localMatch[1]));
    }
    return date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        }).format(date)
      : raw;
  }

  formatJourneyTime(value: any): string {
    const match = String(value || '').match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : String(value || '');
  }

  get totalPayableKSH(): number {
    return Number(this.bookingdetails?.totalfareKSH || 0) +
      Number(this.returndetails?.totalfareKSH || 0);
  }

  get bookingFeeKSH(): number {
    return this.tripBookingFee(this.bookingdetails, 'KSH') +
      this.tripBookingFee(this.returndetails, 'KSH');
  }

  get fareGroups(): Array<{ label: string; count: number; total: number }> {
    return this.tripFareGroups(this.bookingdetails, this.payableCurrency);
  }

  get journeyFareSummaries(): JourneyFareSummary[] {
    return [
      this.buildJourneyFareSummary('Outbound', this.bookingdetails),
      ...(this.is_return && this.returndetails
        ? [this.buildJourneyFareSummary('Return', this.returndetails)]
        : [])
    ];
  }

  private buildJourneyFareSummary(label: string, details: any): JourneyFareSummary {
    const currency = this.payableCurrency;
    return {
      label,
      details,
      from: details?.search_from || details?.result?.from || '',
      to: details?.search_to || details?.result?.to || '',
      date: this.journeyDate(details),
      seats: details?.seatsSelected || [],
      fareGroups: this.tripFareGroups(details, currency),
      bookingFee: this.tripBookingFee(details, currency),
      total: currency === 'UGX'
        ? Number(details?.totalfareUGX || 0)
        : Number(details?.totalfareKSH || 0)
    };
  }

  private tripBookingFee(details: any, currency: 'KSH' | 'UGX'): number {
    if (currency === 'UGX') {
      return Number(
        details?.booking_fee_ugx ??
        details?.result?.booking_fee_ugx ??
        details?.result?.service_fee_ugx ??
        0
      ) || 0;
    }
    return Number(
      details?.booking_fee ?? details?.result?.booking_fee ?? details?.result?.service_fee ?? 0
    ) || 0;
  }

  private tripFareGroups(
    details: any,
    currency: 'KSH' | 'UGX'
  ): Array<{ label: string; count: number; total: number }> {
    const groups = new Map<string, { label: string; count: number; total: number }>();
    (details?.seatsSelected || []).forEach((seat: any) => {
      const type = String(seat?.space_type || 'standard').toLowerCase();
      const label = type === 'normal' ? 'Standard' :
        type.charAt(0).toUpperCase() + type.slice(1);
      const current = groups.get(label) || { label, count: 0, total: 0 };
      current.count += 1;
      current.total += Number(currency === 'UGX' ? seat?.fareUGX : seat?.fareKSH) || 0;
      groups.set(label, current);
    });

    const rows = Array.from(groups.values());
    if (rows.length === 1 && !rows[0].total) {
      const total = currency === 'UGX'
        ? Number(details?.totalfareUGX || 0)
        : Number(details?.totalfareKSH || 0);
      rows[0].total = Math.max(0, total - this.tripBookingFee(details, currency));
    }
    return rows;
  }

  get passengerSeats(): any[] {
    const outboundSeats = this.bookingdetails?.seatsSelected || [];
    const returnSeats = this.returndetails?.seatsSelected || [];
    const additionalReturnPassengers = returnSeats
      .slice(outboundSeats.length)
      .map((seat: any, index: number) => ({
        ...seat,
        id: `return-passenger-${outboundSeats.length + index}-${seat.id}`,
        actualSeatId: seat.id,
        passengerJourney: 'Return'
      }));
    return [...outboundSeats, ...additionalReturnPassengers];
  }

  passengerSeatLabel(index: number): string {
    const outboundSeat = this.bookingdetails?.seatsSelected?.[index];
    const returnSeat = this.returndetails?.seatsSelected?.[index];
    return [
      outboundSeat ? `Outbound seat ${outboundSeat.id}` : '',
      returnSeat ? `Return seat ${returnSeat.id}` : ''
    ].filter(Boolean).join(' · ');
  }

  private passengerSeatSummary(index: number): string {
    const outboundSeat = this.bookingdetails?.seatsSelected?.[index];
    const returnSeat = this.returndetails?.seatsSelected?.[index];
    if (!returnSeat) {
      return String(outboundSeat?.id ?? 'Not assigned');
    }
    return this.passengerSeatLabel(index);
  }

  passengerIsComplete(seat: any): boolean {
    const passengerFieldsValid = [
      'fname', 'lname', 'doctype', 'idno', 'residence', 'phonecode', 'phoneno'
    ].every(
      prefix => this.paymentForm.get(prefix + seat.id)?.valid
    );
    const isFirstPassenger = String(this.passengerSeats[0]?.id) === String(seat?.id);
    return passengerFieldsValid && (!isFirstPassenger || !!this.paymentForm.get('email')?.valid);
  }

  passengerName(seat: any): string {
    const firstName = this.paymentForm.get('fname' + seat.id)?.value || '';
    const lastName = this.paymentForm.get('lname' + seat.id)?.value || '';
    return `${firstName} ${lastName}`.trim();
  }

  get bookingSummaryPassengers(): BookingSummaryPassenger[] {
    const seats = this.passengerSeats;
    const useUGX = this.payableCurrency === 'UGX';
    return seats.map(
      (seat: any, index: number) => ({
        fullName: this.passengerName(seat) || `Passenger ${index + 1}`,
        phoneNumber: this.paymentForm?.get('phoneno' + seat.id)?.value
          ? '+' + this.normalizePassengerPhone(
              this.paymentForm.get('phoneno' + seat.id)?.value,
              this.paymentForm.get('phonecode' + seat.id)?.value
            )
          : 'Not provided',
        seatNumber: this.passengerSeatSummary(index),
        amount: this.passengerFare(index, useUGX ? 'UGX' : 'KSH')
      })
    );
  }

  private passengerFare(index: number, currency: 'KSH' | 'UGX'): number {
    const fareKey = currency === 'UGX' ? 'fareUGX' : 'fareKSH';
    return Number(this.bookingdetails?.seatsSelected?.[index]?.[fareKey] || 0) +
      Number(this.returndetails?.seatsSelected?.[index]?.[fareKey] || 0);
  }

  togglePassenger(index: number): void {
    this.expandedPassenger = this.expandedPassenger === index ? -1 : index;
  }

  savePassenger(index: number): void {
    const seat = this.passengerSeats[index];
    ['fname', 'lname', 'doctype', 'idno', 'residence', 'phonecode', 'phoneno'].forEach(
      prefix => this.paymentForm.get(prefix + seat.id)?.markAsTouched()
    );
    if (index === 0) {
      this.paymentForm.get('email')?.markAsTouched();
    }
    if (!this.passengerIsComplete(seat)) {
      return;
    }
    const next = this.passengerSeats.findIndex(
      (candidate: any, candidateIndex: number) =>
        candidateIndex > index && !this.passengerIsComplete(candidate)
    );
    this.expandedPassenger = next;
  }

  get primaryPassengerName(): string {
    const firstSeat = this.passengerSeats[0];
    return firstSeat ? this.passengerName(firstSeat) : '';
  }

  get boardingPointOptions(): any[] {
    const options = this.bookingdetails?.result?.boarding_points;
    return Array.isArray(options) && options.length ? options : [this.bookingdetails?.pickup_location];
  }

  get dropoffPointOptions(): any[] {
    const options = this.bookingdetails?.result?.dropoff_points;
    return Array.isArray(options) && options.length ? options : [this.bookingdetails?.dropoff_location];
  }

  pointLabel(point: any): string {
    return typeof point === 'string' ? point : point?.name || point?.title || point?.location || '';
  }

  beginBoardingEdit(): void {
    this.draftPickupLocation = this.bookingdetails?.pickup_location || '';
    this.draftDropoffLocation = this.bookingdetails?.dropoff_location || '';
    this.editingBoarding = true;
  }

  cancelBoardingEdit(): void {
    this.editingBoarding = false;
  }

  saveBoardingSelection(): void {
    if (!this.draftPickupLocation || !this.draftDropoffLocation) {
      return;
    }
    this.bookingdetails.pickup_location = this.draftPickupLocation;
    this.bookingdetails.dropoff_location = this.draftDropoffLocation;
    this.persistBoardingSelection();
    this.editingBoarding = false;
  }

  continueToPayment(): void {
    this.paymentForm.get('email')?.markAsTouched();
    this.passengerSeats.forEach((seat: any) => {
      ['fname', 'lname', 'doctype', 'idno', 'residence', 'phonecode', 'phoneno'].forEach(
        prefix => this.paymentForm.get(prefix + seat.id)?.markAsTouched()
      );
    });

    const incompletePassenger = this.passengerSeats.findIndex(
      (seat: any) => !this.passengerIsComplete(seat)
    );
    if (incompletePassenger >= 0) {
      this.expandedPassenger = incompletePassenger;
      return;
    }

    if (!this.paymentForm.get('mobileNumber')?.value) {
      const firstSeat = this.passengerSeats[0];
      this.paymentForm.get('mobileNumber')?.setValue(
        firstSeat ? this.paymentForm.get('phoneno' + firstSeat.id)?.value || '' : ''
      );
    }
    this.currentStep = 'payment';
    this.savePaymentFlow();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  backToPassenger(): void {
    this.currentStep = 'passenger';
    this.savePaymentFlow();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  backToBuses(): void {
    this.navigateToSearchResults(false);
  }

  chooseAnotherSeat(): void {
    this.navigateToSearchResults(true);
  }

  cancelSeatConflict(): void {
    this.seatConflictOpen = false;
    this.navigateToSearchResults(false);
  }

  private navigateToSearchResults(openSeatDrawer: boolean): void {
    const date = this.bookingdetails?.search_details?.traveldate ||
      this.bookingdetails?.result?.departure_date;
    const searchPayload = this.searchResultsPayload();
    if (searchPayload) {
      this.dataService.changeMessage(searchPayload);
    }
    if (openSeatDrawer) {
      const result = this.bookingdetails?.result || {};
      const recovery: SeatConflictRecovery = {
        tripId: String(result.trip_id || ''),
        routeScheduleId: String(result.route_schedule_id || ''),
        busId: String(result.id || result.bus_id || result.fleet_registration_id || ''),
        operatorAlias: String(result.operator?.alias || ''),
        selectedBus: result
      };
      sessionStorage.setItem(SEAT_CONFLICT_RECOVERY_STORAGE_KEY, JSON.stringify(recovery));
      this.dataService.setPaymentFormState(this.seatConflictFormState());
    } else {
      sessionStorage.removeItem(SEAT_CONFLICT_RECOVERY_STORAGE_KEY);
      this.dataService.clearPaymentFormState();
    }
    this.router.navigate(date ? ['/booking', date] : ['/']);
  }

  private searchResultsPayload(): string | null {
    const searchDetails = this.bookingdetails?.search_details;
    if (this.isValidSearchDetails(searchDetails)) {
      return Base64.encode(JSON.stringify(searchDetails));
    }

    const storedPayload = localStorage.getItem('search-mashpoa');
    if (!storedPayload) {
      return null;
    }
    try {
      const decoded = JSON.parse(Base64.decode(storedPayload));
      return this.isValidSearchDetails(decoded) ? storedPayload : null;
    } catch {
      return null;
    }
  }

  private isValidSearchDetails(value: any): boolean {
    return !!value && !!value.traveldate &&
      value.pickup !== undefined && value.dropoff !== undefined &&
      !!value.pickupname && !!value.dropoffname;
  }

  private handleBookingFailure(message: unknown): void {
    const readableMessage = Array.isArray(message)
      ? String(message[0] || '')
      : String(message || 'We could not complete this booking. Please try again.');
    this.loading = false;
    this.isError = true;
    this.errorMessage = readableMessage;
    if (this.isSeatConflict(readableMessage)) {
      this.seatConflictMessage = readableMessage;
      this.seatConflictOpen = true;
    }
  }

  private isSeatConflict(message: string): boolean {
    return /(seat|chair).*(selected|booked|taken|occupied|unavailable|not available)|already.*(seat|chair)/i.test(message);
  }

  private seatConflictFormState(): Record<string, unknown> {
    return {
      ...this.paymentForm.getRawValue(),
      seatConflictPassengers: (this.bookingdetails?.seatsSelected || []).map((seat: any) => ({
        fname: this.paymentForm.get('fname' + seat.id)?.value || '',
        lname: this.paymentForm.get('lname' + seat.id)?.value || '',
        doctype: this.paymentForm.get('doctype' + seat.id)?.value || '',
        idno: this.paymentForm.get('idno' + seat.id)?.value || '',
        residence: this.paymentForm.get('residence' + seat.id)?.value || '',
        phonecode: this.paymentForm.get('phonecode' + seat.id)?.value || '',
        phoneno: this.paymentForm.get('phoneno' + seat.id)?.value || '',
        assistance: this.paymentForm.get('assistance' + seat.id)?.value || ''
      }))
    };
  }

  private restoreSeatConflictPassengers(state: Record<string, unknown>): void {
    const passengers = state['seatConflictPassengers'];
    if (!Array.isArray(passengers)) {
      return;
    }
    (this.bookingdetails?.seatsSelected || []).forEach((seat: any, index: number) => {
      const passenger = passengers[index];
      if (!passenger || typeof passenger !== 'object') {
        return;
      }
      const passengerFields = passenger as Record<string, unknown>;
      [
        'fname', 'lname', 'doctype', 'idno', 'residence', 'phonecode', 'phoneno', 'assistance'
      ].forEach(prefix => {
        this.paymentForm.get(prefix + seat.id)?.setValue(passengerFields[prefix] || '');
      });
    });
  }

  private restorePaymentFlow(): void {
    this.removeSessionItem(LEGACY_PAYMENT_FORM_STORAGE_KEY);
    const savedFlow = this.readSessionItem(PAYMENT_FLOW_STORAGE_KEY);
    if (savedFlow) {
      try {
        const state = JSON.parse(savedFlow) as Partial<StoredPaymentFlow>;
        const currentSeatIds = this.selectedSeatIds();
        const storedSeatIds = Array.isArray(state.seatIds)
          ? state.seatIds.map(id => String(id))
          : [];
        if (
          state.version === 1 &&
          storedSeatIds.length === currentSeatIds.length &&
          storedSeatIds.every((id, index) => id === currentSeatIds[index])
        ) {
          this.accepted_terms = state.acceptedTerms === true;
          this.currentStep = state.currentStep === 'payment' ? 'payment' : 'passenger';
        }
      } catch {
        this.removeSessionItem(PAYMENT_FLOW_STORAGE_KEY);
      }
    }

    const requestedStep = this.readSessionItem(PAYMENT_FLOW_STEP_STORAGE_KEY);
    if (requestedStep === 'payment') {
      this.currentStep = 'payment';
    } else if (requestedStep === 'passenger') {
      this.currentStep = 'passenger';
    }
    this.removeSessionItem(PAYMENT_FLOW_STEP_STORAGE_KEY);

    // Passenger fields are intentionally not persisted. Never strand a user on
    // the payment step with required passenger details missing after a reload/back.
    if (
      this.currentStep === 'payment' &&
      this.passengerSeats.some(
        (seat: any) => !this.passengerIsComplete(seat)
      )
    ) {
      this.currentStep = 'passenger';
    }
  }

  private savePaymentFlow(): void {
    const state: StoredPaymentFlow = {
      version: 1,
      currentStep: this.currentStep,
      acceptedTerms: this.accepted_terms,
      seatIds: this.selectedSeatIds()
    };
    this.writeSessionItem(PAYMENT_FLOW_STORAGE_KEY, JSON.stringify(state));
  }

  private selectedSeatIds(): string[] {
    if (!this.returndetails?.seatsSelected?.length) {
      return (this.bookingdetails?.seatsSelected || []).map((seat: any) =>
        String(seat?.id ?? seat?.seat_number ?? seat?.number ?? '')
      );
    }
    return [
      ...(this.bookingdetails?.seatsSelected || []).map((seat: any) => `outbound:${seat?.id}`),
      ...(this.returndetails?.seatsSelected || []).map((seat: any) => `return:${seat?.id}`)
    ];
  }

  private clearPaymentFlowStorage(): void {
    this.removeSessionItem(PAYMENT_FLOW_STORAGE_KEY);
    this.removeSessionItem(LEGACY_PAYMENT_FORM_STORAGE_KEY);
    this.removeSessionItem(PAYMENT_FLOW_STEP_STORAGE_KEY);
  }

  private readSessionItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeSessionItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // The flow remains usable when storage is disabled.
    }
  }

  private removeSessionItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts.
    }
  }

  private persistBoardingSelection(): void {
    const storedPayload = localStorage.getItem('payments-mashpoa');
    if (!storedPayload) {
      return;
    }
    try {
      const checkout = JSON.parse(Base64.decode(storedPayload));
      const travel = JSON.parse(Base64.decode(checkout.travel));
      travel.pickup_location = this.bookingdetails.pickup_location;
      travel.dropoff_location = this.bookingdetails.dropoff_location;
      checkout.travel = Base64.encode(JSON.stringify(travel));
      localStorage.setItem('payments-mashpoa', Base64.encode(JSON.stringify(checkout)));
    } catch {
      // Keep the active in-memory booking usable if legacy storage is malformed.
    }
  }

copyDetails(){
  let seatno=0;
  let fsname: string, lsname: string, isdno: string, doctype: string;
  let phoneno: string, phonecode: string, residence:string;
  let passenger
    this.passengerSeats.forEach((seat) => {
      seatno++;
      let passenger={};
      this.fsname='fname'+seat.id;
      this.lsname='lname'+seat.id;
      this.isdno='idno'+seat.id;
      const documentTypeControl = 'doctype'+seat.id;
      this.phoneno = 'phoneno'+seat.id;
      const phoneCodeControl = 'phonecode'+seat.id;
      this.residence='residence'+seat.id;
      if( seatno == 1){
        fsname=this.paymentForm.controls[this.fsname].value
        lsname=this.paymentForm.controls[this.lsname].value
        isdno=this.paymentForm.controls[this.isdno].value
        doctype=this.paymentForm.controls[documentTypeControl].value
        phoneno=this.paymentForm.controls[this.phoneno].value
        phonecode=this.paymentForm.controls[phoneCodeControl].value
        residence=this.paymentForm.controls[this.residence].value
      }

      if (seatno > 1){
        this.paymentForm.get(this.fsname).setValue(fsname);
        this.paymentForm.get(this.lsname).setValue(lsname);
        this.paymentForm.get(this.isdno).setValue(isdno);
        this.paymentForm.get(documentTypeControl).setValue(doctype);
        this.paymentForm.get(this.phoneno).setValue(phoneno);
        this.paymentForm.get(phoneCodeControl).setValue(phonecode);
        this.paymentForm.get(this.residence).setValue(residence);
      }
    });
    this.copyMessage ='Copied!'
    setTimeout(() => {
      this.copyMessage ='Copy traveller 1 details'
    }, 2000);

}

acceptedTerms(){
  this.accepted_terms=!this.accepted_terms;
  this.savePaymentFlow();
}


submitForm(){
  if (this.loading) {
    return;
  }
  this.submitted=true;
  if(!this.accepted_terms){

    return
  }

if (this.paymentForm.invalid ) {
  return;
}

  const paymentMethod = this.paymentForm.get('paymentMethod').value;
  // The v2 selection combines provider and currency (for example airtel:UGX).
  // Send the exact provider returned by the API alongside the selected currency.
  const selectedPaymentOption = this.paymentOptionFor(paymentMethod);
  const paymentChannel = selectedPaymentOption?.paymentOption || '';
  const paymentType = selectedPaymentOption?.name || paymentChannel;
  



  let value = this.paymentForm.value;
  value.fullname = this.primaryPassengerName;
  const primarySeat = this.bookingdetails.seatsSelected[0];
  const primaryResidence = value['residence'+primarySeat.id];
  this.isError=false;
  this.loading=true;
  this.passengers=[];
  this.bookingdetails.seatsSelected.forEach((seat) => {
    let passenger={};
    this.fsname='fname'+seat.id;
    this.lsname='lname'+seat.id;
    this.isdno='idno'+seat.id;
    this.phoneno = 'phoneno'+seat.id;
    this.country = 'country'+seat.id;
    this.residence = 'residence'+seat.id;

    
passenger={
    "full_name": value[this.fsname] + ' ' + value[this.lsname],
    "document_type": value['doctype'+seat.id],
    "id_or_passport": value[this.isdno].toString(),
    "seat": {
      "id": seat.id,
      "fare": this.isUgandaPayment(paymentMethod) ? seat.fareUGX : seat.fareKSH,
      "class": seat.space_type
    },
    "phone_number": this.normalizePassengerPhone(
      value[this.phoneno],
      value['phonecode'+seat.id]
    ),
    "residence":  value[this.residence],
}
this.passengers.push(passenger);
});
if(this.is_return){
this.returnpassengers=[];
this.returndetails.seatsSelected.forEach((seat, index) => {
  let passenger={};
  const sourceSeat = this.passengerSeats[index] || this.passengerSeats[0];
  const sourceFirstName = 'fname'+sourceSeat.id;
  const sourceLastName = 'lname'+sourceSeat.id;
  const sourceDocumentType = 'doctype'+sourceSeat.id;
  const sourceDocumentNumber = 'idno'+sourceSeat.id;
  const sourcePhone = 'phoneno'+sourceSeat.id;
  const sourcePhoneCode = 'phonecode'+sourceSeat.id;
  const sourceResidence = 'residence'+sourceSeat.id;

  passenger={
    "full_name": value[sourceFirstName] + ' ' + value[sourceLastName],
    "document_type": value[sourceDocumentType],
    "id_or_passport": value[sourceDocumentNumber].toString(),
    "seat": {
      "id": seat.id,
      "fare": this.isUgandaPayment(paymentMethod) ? seat.fareUGX : seat.fareKSH,
      "class": seat.space_type
    },
    "phone_number": this.normalizePassengerPhone(value[sourcePhone], value[sourcePhoneCode]),
    "residence":  value[sourceResidence]
}
this.returnpassengers.push(passenger);
});
this.bookingdataReturn ={
    "booking_channel": "mashpoa_website",
    "currency": this.payableCurrency,
    "transaction_reference": "",
    "referral_code": "",
    "residence": primaryResidence,
    "promo_code": "",
    "selected_bus": {
      "from": this.returndetails.result.from,
      "to": this.returndetails.result.to,
      "departure_date": this.returndetails.result.departure_date,
      "bus_type_id": this.returndetails.result.bus_type_id,
      "operator": this.returndetails.result.operator,
      "is_express_travel": this.returndetails.result.is_express_travel,
      "number_of_booked_seats": this.returndetails.result.number_of_seats_booked,
      "id": this.returndetails.result.id,
      "make": this.returndetails.result.make,
      "model": this.returndetails.result.model,
      "route_id": this.returndetails.result.route_id,
      "route_schedule_id":  this.returndetails.result.route_schedule_id,
      "route_schedule_code":(this.bookingdetails.result.is_shuttle) ? '0' :  this.returndetails.result.route_schedule_code,
      "departure_time": this.returndetails.result.departure_time,
      "departure_timestamp": this.returndetails.result.departure_timestamp,
      "arrival_time": this.returndetails.result.arrival_time,
      "number_of_available_seats": this.returndetails.result.number_of_available_seats,
      "fare": this.isUgandaPayment(paymentMethod) ? this.returndetails.totalfareUGX : this.returndetails.totalfareKSH,
      "boarding_points": this.returndetails.result.boarding_points,
      "dropoff_points":this.returndetails.result.dropoff_points,
      "seats":this.returndetails.result.seats,
      "seat_types": this.returndetails.result.seats_types,
      "number_of_window_seats": this.returndetails.result.number_of_window_seats,
      "last_booking": this.returndetails.result.last_booking,
      "amenities":this.returndetails.result.amenities,
      "origin_city_id": this.returndetails.result.origin_city_id,
      "destination_city_id": this.returndetails.result.destination_city_id,
      "bus_capacity": this.returndetails.result.bus_capacity,
      "trip_id":this.returndetails.result.trip_id,
      "boarding_point": this.returndetails.pickup_location,
      "drop_off_point": this.returndetails.dropoff_location,
    },
    "payee": {
     "phone_number": this.selectedCountryCode + (value.mobileNumber.length > 9 ? value.mobileNumber.slice(-9) : value.mobileNumber), 
        "name": value.fullname,
         "email": value.email
    },
    "boarding_point": this.returndetails.pickup_location,
    "drop_off_point": this.returndetails.dropoff_location,
    "date_of_travel": this.returndetails.result.departure_date,
    "departure_time": this.returndetails.result.departure_time,
    "arrival_time": this.returndetails.result.arrival_time,
    "route_schedule_id": this.returndetails.result.route_schedule_id,
    "operator": this.returndetails.result.operator,
    "origin_city_name":this.returndetails.result.from,
    "destination_city_name": this.returndetails.result.to,
    "origin_city_id":this.returndetails.result.origin_city_id,
    "destination_city_id": this.returndetails.result.destination_city_id,
    "passengers":  this.returnpassengers,
    "seatsSelected":this.returndetails.seatsSelected,
    "total_fare": this.returndetails.total_fare,
    "children": []
  }

  this.bookingdata ={
    "booking_channel": "mashpoa_website",
    "currency": this.payableCurrency,
    "transaction_reference": "",
    "referral_code": "",
    "residence": primaryResidence,
    "promo_code": "",
    "selected_bus": {
      "from": this.bookingdetails.result.from,
      "to": this.bookingdetails.result.to,
      "departure_date": this.bookingdetails.result.departure_date,
      "bus_type_id": this.bookingdetails.result.bus_type_id,
      "operator": this.bookingdetails.result.operator,
      "is_express_travel": this.bookingdetails.result.is_express_travel,
      "number_of_booked_seats": this.bookingdetails.result.number_of_seats_booked,
      "id": this.bookingdetails.result.id,
      "make": this.bookingdetails.result.make,
      "model": this.bookingdetails.result.model,
      "route_id": this.bookingdetails.result.route_id,
      "route_schedule_id":  this.bookingdetails.result.route_schedule_id,
      "route_schedule_code":(this.bookingdetails.result.is_shuttle) ? '0' :  this.bookingdetails.result.route_schedule_code,
      "departure_time": this.bookingdetails.result.is_shuttle ? '00:00:00' : this.bookingdetails.result.departure_time,
      "departure_timestamp": this.bookingdetails.result.departure_timestamp,
      "arrival_time": this.bookingdetails.result.is_shuttle ? '00:00:00' : this.bookingdetails.result.arrival_time,
      "number_of_available_seats": this.bookingdetails.result.number_of_available_seats,
      "fare": this.isUgandaPayment(paymentMethod) ? this.bookingdetails.totalfareUGX : this.bookingdetails.totalfareKSH,
      "boarding_points": this.bookingdetails.result.boarding_points,
      "dropoff_points":this.bookingdetails.result.dropoff_points,
      "seats":this.bookingdetails.result.seats,
      "seat_types": this.bookingdetails.result.seats_types,
      "number_of_window_seats": this.bookingdetails.result.number_of_window_seats,
      "last_booking": this.bookingdetails.result.last_booking,
      "amenities":this.bookingdetails.result.amenities,
      "origin_city_id":this.bookingdetails.result.origin_city_id,
      "destination_city_id": this.bookingdetails.result.destination_city_id,
      "bus_capacity": this.bookingdetails.result.bus_capacity,
      "trip_id":this.bookingdetails.result.trip_id,
    },
    "payee": {
       "phone_number": this.selectedCountryCode + (value.mobileNumber.length > 9 ? value.mobileNumber.slice(-9) : value.mobileNumber),
        "name": value.fullname,
         "email": value.email
    },
    "boarding_point": this.bookingdetails.pickup,
    "date_of_travel": this.bookingdetails.result.departure_date,
    "departure_time": this.bookingdetails.result.departure_time,
    "arrival_time": this.bookingdetails.result.arrival_time,
    "route_schedule_id":'' + this.bookingdetails.result.route_schedule_id,
    "operator": this.bookingdetails.result.operator,
    "origin_city_name":this.bookingdetails.result.from,
    "destination_city_name": this.bookingdetails.result.to,
    "origin_city_id":this.bookingdetails.result.origin_city_id,
    "destination_city_id": this.bookingdetails.result.destination_city_id,
    "passengers":  this.passengers,
    "children": []
  }



  this.return_trip =  this.bookingdataReturn;
  
}

this.bookingdata ={
  "booking_channel": "mashpoa_website",
  "currency": this.payableCurrency,
  "transaction_reference": "",
  "referral_code": "",
  "residence": primaryResidence,
  "promo_code": "",
  "selected_bus": {
    "from": this.bookingdetails.result.from,
    "to": this.bookingdetails.result.to,
    "departure_date": this.bookingdetails.result.departure_date,
    "bus_type_id": this.bookingdetails.result.bus_type_id,
    "operator": this.bookingdetails.result.operator,
    "is_express_travel": ''+this.bookingdetails.result.is_express_travel,
    "number_of_booked_seats": this.bookingdetails.result.number_of_seats_booked,
    "id": ''+this.bookingdetails.result.id,
    "make": this.bookingdetails.result.make,
    "model": this.bookingdetails.result.model,
    "route_id": this.bookingdetails.result.route_id,
    "route_schedule_id": this.bookingdetails.result.route_schedule_id ,
    "route_schedule_code": (this.bookingdetails.result.is_shuttle) ? '0' : this.bookingdetails.result.route_schedule_code,
    "departure_time": (this.bookingdetails.result.is_shuttle) ? '00:00:00' : this.bookingdetails.result.departure_time,
    "departure_timestamp": this.bookingdetails.result.departure_timestamp,
    "arrival_time":(this.bookingdetails.result.is_shuttle) ? '00:00:00' : this.bookingdetails.result.arrival_time,
    "number_of_available_seats": this.bookingdetails.result.number_of_available_seats,
    "fare": this.isUgandaPayment(paymentMethod) ? this.bookingdetails.totalfareUGX : this.bookingdetails.totalfareKSH,
    "boarding_points": this.bookingdetails.result.boarding_points,
    "dropoff_points":this.bookingdetails.result.dropoff_points,
    "seats":this.bookingdetails.result.seats,
    "seat_types": this.bookingdetails.result.seats_types,
    "number_of_window_seats": this.bookingdetails.result.number_of_window_seats,
    "last_booking": this.bookingdetails.result.last_booking,
    "amenities":this.bookingdetails.result.amenities,
    "origin_city_id":this.bookingdetails.result.origin_city_id,
    "destination_city_id": this.bookingdetails.result.destination_city_id,
    "bus_capacity": this.bookingdetails.result.bus_capacity,
    "trip_id":this.bookingdetails.result.trip_id,
    "boarding_point": this.bookingdetails.pickup_location,
    "drop_off_point": this.bookingdetails.dropoff_location,
  },
  "payee": {
    "phone_number": this.selectedCountryCode + (value.mobileNumber.length > 9 ? value.mobileNumber.slice(-9) : value.mobileNumber),
      "name": value.fullname,
       "email": value.email
  },
  "boarding_point": this.bookingdetails.pickup_location,
  "drop_off_point": this.bookingdetails.dropoff_location,
  "date_of_travel": this.bookingdetails.result.departure_date,
  "departure_time": (this.bookingdetails.result.is_shuttle) ? '00:00:00' : this.bookingdetails.result.departure_time,
  "arrival_time": (this.bookingdetails.result.is_shuttle) ? '00:00:00' : this.bookingdetails.result.arrival_time,
  "route_schedule_id": this.bookingdetails.result.route_schedule_id,
  "operator": this.bookingdetails.result.operator,
  "origin_city_name":this.bookingdetails.result.from,
  "destination_city_name": this.bookingdetails.result.to,
  "origin_city_id":this.bookingdetails.result.origin_city_id,
  "destination_city_id": this.bookingdetails.result.destination_city_id,
  "passengers":  this.passengers,
  "children": []
}







let bookingData:any;

  if(this.is_return){
    this.bookingdata.payment_channel=paymentChannel
    this.return_trip.payment_channel=paymentChannel
    bookingData={
      "initial_trip":this.bookingdata,
      "return_trip":this.return_trip
    }
  }else{
    bookingData=this.bookingdata;
    this.bookingdata.payment_channel=paymentChannel
  }

  this.loading=true;
  this.savePaymentFlow();
  this.service.submitBooking(bookingData).subscribe(
    data  => {
      this.res=data;
      if(this.res.status == true){
        this.dataService.setPaymentFormState(this.paymentForm.getRawValue());
        let others={
          'data':this.res.data,
          'payment_type':paymentType,
          'payment_currency':this.payableCurrency,
          'payment_amount':this.payableAmount,
          'payment_extra_charge':this.paymentExtraCharge,
          'trip_id':this.bookingdetails.result.trip_id,
          'fullname':this.bookingdata.payee.name,
          'email':this.bookingdata.payee.email,
          'phoneno':this.bookingdata.payee.phone_number,
          'depature_date':this.bookingdetails.result.departure_date,
          'departure_time':this.bookingdetails.result.departure_time,
          'arrival_time':this.bookingdetails.result.arrival_time,
          'from':this.bookingdetails.result.from,
          'to':this.bookingdetails.result.to,
          "passengers": this.bookingSummaryPassengers.map(passenger => ({
            full_name: passenger.fullName,
            phone_number: passenger.phoneNumber.replace(/^\+/, ''),
            seat: {
              id: passenger.seatNumber,
              fare: passenger.amount
            }
          })),
          "pickup": this.bookingdetails.search_details.pickup,
          "dropoff": this.bookingdetails.search_details.dropoff,
          "operator": this.bookingdetails.result.operator,
          "is_shuttle":this.bookingdetails.result.is_shuttle,
          "boarding_point": this.bookingdetails.pickup_location,
          "drop_off_point": this.bookingdetails.dropoff_location,

        };
        let data= {
          "bookingdetails":this.bookingdetails,
          "returndetails":this.returndetails || null,
          "returning":!!this.is_return,
          "confirm_data":others
        }

        let f=Base64.encode(JSON.stringify(data))
        this.router.navigate(['/confirm-payments', f])
      }else{
        this.handleBookingFailure(this.res.message);
      }

    },
    error  => {

      if(error.status==500){
        this.handleBookingFailure('Sorry, there has been a technical problem. Please try again.');
      }else{
        this.api_error=new Error().getTheMessage(error?.error?.errors || [error?.error?.message])
        this.handleBookingFailure(this.api_error);
      }

    }

    );

}

}
